import { makeFetchSelectors, makeTypedFetchSelectors } from 'app/redux-fetch'
import { createSelector } from '@reduxjs/toolkit'

import {
  Inventory,
  MistOrganizationPayload,
  SiteDevice,
  OrgTemplates,
  OrgSiteWlans,
  OrgRFTemplate,
  SiteWlan,
  SiteDeviceStats,
  SitePSKs,
  OrgAdmins,
  SiteInfo,
} from 'features/networkAi/types'
import { selectedHomeSelector } from 'features/home'
import { SubscriptionInfo } from 'features/home/types'
import {
  DOMAINS_REDUCER_NAME,
  MIST_ORGANIZATION_REDUCER_NAME,
} from 'features/domains/consts'
import {
  CLAIM_DEVICES_REDUCER_NAME,
  INVENTORY_REDUCER_NAME,
  MIST_ORGANIZATION_DATA_REDUCER_NAME,
  MIST_SITE_DATA_REDUCER_NAME,
  NETWORK_AI_REDUCER_NAME,
  SITE_INFO_REDUCER_NAME,
  SITE_WLANS_REDUCER_NAME,
  SITE_CLIENTS_REDUCER_NAME,
  ORG_TEMPLATES_REDUCER_NAME,
  ORG_SITE_WLANS_REDUCER_NAME,
  ORG_RF_TEMPLATES_REDUCER_NAME,
  SITE_DEVICE_STATS_REDUCER_NAME,
  SITE_PSKS_REDUCER_NAME,
  ORG_ADMINS_REDUCER_NAME,
} from './consts'
import { SiteClients } from './NetworkDashboard/NetworkClients/types'

export const mistOrganizationFetchDataSelector =
  makeTypedFetchSelectors<MistOrganizationPayload>(
    DOMAINS_REDUCER_NAME,
    MIST_ORGANIZATION_REDUCER_NAME,
    MIST_ORGANIZATION_DATA_REDUCER_NAME
  )

export const mistOrganizationIdSelector = createSelector(
  mistOrganizationFetchDataSelector.data,
  (data) => data?.orgId || ''
)

export const mistSiteFetchDataSelector = makeFetchSelectors(
  DOMAINS_REDUCER_NAME,
  MIST_ORGANIZATION_REDUCER_NAME,
  MIST_SITE_DATA_REDUCER_NAME
)

// SiteId is an id of Network home in Mist
export const mistSiteIdSelector = createSelector(
  mistSiteFetchDataSelector.data,
  (data) => data?.id || ''
)

export const claimDevicesFetchDataSelector = makeTypedFetchSelectors<
  SiteDevice[]
>(NETWORK_AI_REDUCER_NAME, CLAIM_DEVICES_REDUCER_NAME)

export const inventoryFetchDataSelector = makeTypedFetchSelectors<Inventory[]>(
  NETWORK_AI_REDUCER_NAME,
  INVENTORY_REDUCER_NAME
)

export const siteInfoFetchDataSelector = makeTypedFetchSelectors<SiteInfo>(
  NETWORK_AI_REDUCER_NAME,
  SITE_INFO_REDUCER_NAME
)

export const siteWlansFetchDataSelector = makeTypedFetchSelectors<SiteWlan[]>(
  NETWORK_AI_REDUCER_NAME,
  SITE_WLANS_REDUCER_NAME
)

export const siteClientsFetchDataSelector = makeTypedFetchSelectors<
  SiteClients[]
>(NETWORK_AI_REDUCER_NAME, SITE_CLIENTS_REDUCER_NAME)

export const siteDeviceStatsFetchDataSelector = makeTypedFetchSelectors<
  SiteDeviceStats[]
>(NETWORK_AI_REDUCER_NAME, SITE_DEVICE_STATS_REDUCER_NAME)

export const orgTemplatesFetchDataSelector = makeTypedFetchSelectors<
  OrgTemplates[]
>(NETWORK_AI_REDUCER_NAME, ORG_TEMPLATES_REDUCER_NAME)

export const orgSiteWlansFetchDataSelector = makeTypedFetchSelectors<
  OrgSiteWlans[]
>(NETWORK_AI_REDUCER_NAME, ORG_SITE_WLANS_REDUCER_NAME)

export const orgRFTemplatesFetchDataSelector =
  makeTypedFetchSelectors<OrgRFTemplate>(
    NETWORK_AI_REDUCER_NAME,
    ORG_RF_TEMPLATES_REDUCER_NAME
  )

export const sitePSKsFetchDataSelector = makeTypedFetchSelectors<SitePSKs[]>(
  NETWORK_AI_REDUCER_NAME,
  SITE_PSKS_REDUCER_NAME
)

export const orgAdminsFetchDataSelector = makeTypedFetchSelectors<OrgAdmins[]>(
  NETWORK_AI_REDUCER_NAME,
  ORG_ADMINS_REDUCER_NAME
)

export const subscribedAccessPointsSelector = createSelector(
  selectedHomeSelector.data,
  (selectedHome) => {
    if (selectedHome?.subscriptionInfo) {
      const networkSubscription = selectedHome.subscriptionInfo.find(
        (l) => l.category === 'NETWORK'
      )
      return networkSubscription || ({} as SubscriptionInfo)
    }
    return {} as SubscriptionInfo
  }
)

export const subscribedEssentialsSelector = createSelector(
  selectedHomeSelector.data,
  (selectedHome) => {
    if (selectedHome?.subscriptionInfo) {
      const networkSubscription = selectedHome.subscriptionInfo.find(
        (l) => l.category === 'ESSENTIALS'
      )
      return networkSubscription || ({} as SubscriptionInfo)
    }
    return {} as SubscriptionInfo
  }
)

export const subsribedAccessPointsCountSelector = createSelector(
  selectedHomeSelector.data,
  (selectedHome) => {
    if (selectedHome?.subscriptionInfo) {
      const networkSubscription = selectedHome.subscriptionInfo.find(
        (l) => l.category === 'NETWORK'
      )
      return networkSubscription?.count || 0
    }
    return 0
  }
)

export const homeHostMachineTypeSelector = createSelector(
  selectedHomeSelector.data,
  (selectedHome) => {
    if (selectedHome?.hostInfo) {
      return selectedHome?.hostInfo.hostMachineType || 0
    }
    return 0
  }
)

export const sitePKSsSsidSelector = (ssid: string) =>
  createSelector(sitePSKsFetchDataSelector.data, (sitePSKs) => {
    let ssidPSKs: SitePSKs[] = []

    if (sitePSKs) {
      sitePSKs.forEach(function (value) {
        if (value.ssid === ssid) {
          ssidPSKs.push(value)
        }
      })
      return ssidPSKs
    }
    return ssidPSKs
  })
